/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {ReportService} from '../../../../services/report.service';
import {ReportHistoryProvider, ReportProvider} from '../../../../providers';
import {Helpers, Report} from '../../../../model';
import {HistorySummary, History, HistoryType} from '../../../../model/history';
import { SearchableItem } from 'src/app/model/interface/searchable.item';
import { PresetValue } from 'src/app/model/preset-value';
import { ModuleType } from 'src/app/model/enums/module.type';
import { ActivatedRoute } from '@angular/router';
import { LogService } from 'src/app/services';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ReportHistoryModal } from 'src/app/components/modal/report-history/report-history.modal';

@Component({
  selector: 'page-report-history',
  templateUrl: 'report.history.page.html',
  styleUrls: ['report.history.page.scss']
})
export class ReportHistoryPage implements OnInit, OnDestroy {
  currentReport: Report;
  private reportSub: any;

  history: History[] = [];
  summary: HistorySummary[] = [];

  totalItems = 0;
  currentPage = 1;
  queryString = '';
  type: HistoryType = null;
  dateFilter: string = null;
  HistoryType = HistoryType;
  modalRef: BsModalRef;


  isLoading = false;

  historyTypes: SearchableItem[] = [
    PresetValue.create('Data', HistoryType.DATA),
    PresetValue.create('Stage', HistoryType.STAGE),
    PresetValue.create('Status', HistoryType.STATUS),
    PresetValue.create('Number', HistoryType.NUMBER),
    PresetValue.create('Ask Questions', HistoryType.ASK_QUESTIONS)
  ];

  selectedModuleType: ModuleType = ModuleType.ALL;

  moduleTypes: SearchableItem[] = [
    PresetValue.create('All', ModuleType.ALL),
    PresetValue.create('PMA', ModuleType.PMA),
    PresetValue.create('BVQ', ModuleType.BVQ),
  ];

  constructor(
    private route: ActivatedRoute,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    public reportHistoryProvider: ReportHistoryProvider,
    private logService : LogService,
    private modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    this.selectedModuleType = this.route.snapshot.data.pma ? ModuleType.PMA : ModuleType.BVQ;

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport)
        this.loadHistorySummary(this.currentReport.ID, 1, null, this.selectedModuleType);
    });

  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
  }

  resetHistoryView(): void {
    this.queryString = '';
    this.dateFilter = null;
    this.loadHistorySummary(this.currentReport.ID, 1, this.type, this.selectedModuleType);
  }

  loadHistorySummary(id: string, page: number, type?: HistoryType, module?: ModuleType): void {
    this.isLoading = true;
    this.history = [];
    this.totalItems = 0;

    this.reportHistoryProvider.getHistorySummary(id, page, type, module)
      .subscribe(
        data => {
          this.isLoading = false;

          if (data.success) {
            this.summary = HistorySummary.parseArray(data.result.items);
            this.totalItems = data.result.totalItems;

            this.currentPage = page;
          }
        }, err => {
          this.isLoading = false;
          this.logService.error(err);
        });
  }

  loadHistory(id: string, page: number, incDate: string, type?: HistoryType, moduleType?: ModuleType): void {
    this.isLoading = true;
    this.history = [];
    this.totalItems = 0;

    this.reportHistoryProvider.getHistory(id, page, this.queryString, incDate, type, moduleType)
      .subscribe(
        data => {
          this.isLoading = false;

          if (data.success) {
            this.history = History.parseArray(data.result.items);
            this.totalItems = data.result.totalItems;

            this.currentPage = page;
          }
        }, err => {
          this.isLoading = false;
          this.logService.error(err);
        });
  }

  pageChanged(event: any): void {
    if (event.page != this.currentPage)
      this.loadHistory(this.currentReport.ID, event.page, this.dateFilter, this.type, this.selectedModuleType);
  }

  searchChanged(value: any): void {
    this.queryString = value;
    this.loadHistory(this.currentReport.ID, 1, this.dateFilter, this.type, this.selectedModuleType);
  }

  setDate(date: string): void {
    this.dateFilter = date;
    this.queryString = '';
    this.loadHistory(this.currentReport.ID, 1, this.dateFilter, this.type, this.selectedModuleType);
  }

  typeChanged(value: any): void {
    this.type = value != null ? value.VALUE : null;
    this.loadHistorySummary(this.currentReport.ID, 1, this.type, this.selectedModuleType);
  }

  getTypeName(value: any): string {
    if (this.historyTypes) {
      const preset = this.historyTypes.find(h => h.getValue() === value);
      if (preset)
        return preset.getName();

      return null;

    }
  }

  moduleChanged(value: any): void {
    this.selectedModuleType = value != null && value.VALUE !== "ALL" ? value.VALUE : null;
    this.loadHistorySummary(this.currentReport.ID, 1, this.type, this.selectedModuleType)
  }

  showReportHistory(historyId: string): void {
    this.modalRef = this.modalService.show(ReportHistoryModal, Helpers.getModalConfig());
    this.modalRef.content.reportId = this.currentReport.ID;
    this.modalRef.content.historyId = historyId;
  }

}
